import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Send, Users, Eye, RefreshCw, Mail, MousePointerClick } from "lucide-react";
import { api } from "../../lib/api";

const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet text-sm";

export default function ModuleEmail({ moduleKey, item, onRefresh }) {
  const [asunto, setAsunto] = useState(item.asunto || "");
  const [contenido, setContenido] = useState(item.contenido || "");
  const [sources, setSources] = useState([]);
  const [picked, setPicked] = useState({});
  const [extra, setExtra] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const recipients = item.recipients || [];

  useEffect(() => {
    api.get(`/mod/${moduleKey}/audience`).then((r) => setSources(r.data.sources || [])).catch(() => {});
  }, [moduleKey]);

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const total = sources.reduce((n, s) => (picked[s.key] ? n + s.count : n), 0) +
    extra.split(/[,;\s]+/).filter(validEmail).length;

  const genAI = async () => {
    setAiBusy(true);
    try {
      await api.patch(`/mod/${moduleKey}/items/${item.id}`, { asunto, contenido });
      const { data } = await api.post(`/mod/${moduleKey}/items/${item.id}/ai`);
      setContenido(data.text || contenido);
      toast.success("Contenido generado con IA");
      onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al generar"); }
    finally { setAiBusy(false); }
  };

  const send = async () => {
    if (!asunto.trim()) return toast.error("Escribe el asunto");
    if (!contenido.trim()) return toast.error("Escribe el contenido");
    const recs = [];
    sources.forEach((s) => { if (picked[s.key]) recs.push(...s.recipients); });
    if (recs.length === 0 && !extra.trim()) return toast.error("Selecciona una audiencia");
    setSending(true);
    try {
      const { data } = await api.post(`/mod/${moduleKey}/items/${item.id}/send`, {
        asunto, contenido, recipients: recs, extra_emails: extra,
      });
      toast.success(`Enviado a ${data.enviados} destinatario(s)`);
      onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al enviar"); }
    finally { setSending(false); }
  };

  return (
    <div className="mt-4 border-t border-navy/10 pt-4 space-y-4" data-testid="module-email">
      <div className="flex items-center gap-2 overline text-navy/40"><Mail className="w-3.5 h-3.5" /> Compositor de correo</div>

      <input value={asunto} onChange={(e) => setAsunto(e.target.value)} placeholder="Asunto del correo"
        className={input} data-testid="email-asunto" />
      <textarea value={contenido} onChange={(e) => setContenido(e.target.value)} rows={5} data-testid="email-contenido"
        placeholder="Escribe el cuerpo del correo. Puedes usar HTML (encabezados, botones, imágenes) o texto normal."
        className={`${input} font-mono text-[13px]`} />
      <div className="flex gap-2">
        <button onClick={genAI} disabled={aiBusy} className="btn btn-outline" data-testid="email-ai-btn">
          {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generar con IA
        </button>
      </div>

      {contenido && (
        <div className="rounded-lg overflow-hidden border border-navy/10" data-testid="email-preview">
          <div className="bg-navy/5 px-3 py-1.5 text-[11px] text-navy/40">Vista previa</div>
          <iframe title="email-preview" srcDoc={contenido.includes("<") ? contenido : contenido.replace(/\n/g, "<br>")}
            className="w-full h-56 bg-white" sandbox="" />
        </div>
      )}

      {/* Audiencia */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 overline text-navy/40"><Users className="w-3.5 h-3.5" /> Audiencia</div>
        {sources.map((s) => (
          <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`email-src-${s.key}`}>
            <input type="checkbox" checked={!!picked[s.key]} disabled={s.count === 0}
              onChange={(e) => setPicked({ ...picked, [s.key]: e.target.checked })} />
            <span className="text-navy/70">{s.label}</span>
            <span className="text-navy/40">({s.count})</span>
          </label>
        ))}
        <textarea value={extra} onChange={(e) => setExtra(e.target.value)} rows={2} data-testid="email-extra"
          placeholder="Correos adicionales, separados por coma"
          className={`${input} text-[13px]`} />
      </div>

      <div className="flex items-center gap-3">
        <button onClick={send} disabled={sending || total === 0} className="btn btn-primary" data-testid="email-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar ({total})
        </button>
      </div>

      {/* Resultados / tracking */}
      {recipients.length > 0 && (
        <div className="space-y-2" data-testid="email-results">
          <div className="flex items-center justify-between">
            <div className="flex gap-4 text-sm flex-wrap">
              <span className="text-navy/60">Enviados: <b className="text-navy">{item.enviados || 0}</b></span>
              <span className="text-navy/60"><Eye className="w-3.5 h-3.5 inline" /> Aperturas: <b className="text-violet">{item.aperturas || 0}</b></span>
              <span className="text-navy/60"><MousePointerClick className="w-3.5 h-3.5 inline" /> Clics: <b className="text-violet">{item.clics || 0}</b></span>
              <span className="text-navy/60">Bajas: <b className="text-destructive">{item.bajas || 0}</b></span>
            </div>
            <button onClick={onRefresh} className="btn btn-ghost text-xs" data-testid="email-refresh-btn"><RefreshCw className="w-3.5 h-3.5" /> Actualizar</button>
          </div>
          <div className="card-surface overflow-x-auto max-h-56 overflow-y-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-navy/10 text-navy/50"><th className="text-left px-3 py-2">Destinatario</th><th className="text-left px-3 py-2">Correo</th><th className="text-left px-3 py-2">Estado</th><th className="text-left px-3 py-2">Abierto</th><th className="text-left px-3 py-2">Clic</th><th className="text-left px-3 py-2">Baja</th></tr></thead>
              <tbody>
                {recipients.map((r) => (
                  <tr key={r.rid} className="border-b border-navy/5 last:border-0">
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2 text-navy/60">{r.email}</td>
                    <td className="px-3 py-2">{r.status === "success" ? "Enviado" : r.status === "skipped" ? "Simulado" : "Falló"}</td>
                    <td className="px-3 py-2">{r.opened ? <span className="text-violet font-medium">Sí</span> : <span className="text-navy/30">—</span>}</td>
                    <td className="px-3 py-2">{r.clicked ? <span className="text-violet font-medium">Sí</span> : <span className="text-navy/30">—</span>}</td>
                    <td className="px-3 py-2">{r.unsubscribed ? <span className="text-destructive font-medium">Sí</span> : <span className="text-navy/30">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
